import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { User, UserRole, UserRoleColors, UserRoleSelectOptions } from "../../models/User";
import { editUserSchema } from "@/lib/schemas/userSchemas";
import GenericMultipleSelect from "../common/GenericMultipleSelect";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { useQuery } from "@tanstack/react-query";
import { OrganizationName } from "../../models/Organization";
import { setApiErrors } from "@/lib/form-utils";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface EditUserFormValues {
  username: string;
  email: string;
  role: UserRole;
  managedOrganizations: string[];
}

interface EditUserModalProps {
  open: boolean;
  handleClose: () => void;
  user: User | null;
  onEditUser: (updatedUser: User) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  handleClose,
  user,
  onEditUser,
}) => {
  const organizationsRepository = new OrganizationsRepository();

  const form = useForm<EditUserFormValues>({
    defaultValues: {
      username: "",
      email: "",
      role: UserRole.Viewer,
      managedOrganizations: [],
    },
    resolver: zodResolver(editUserSchema),
  });

  const watchedRole = form.watch("role");
  const watchedManagedOrgs = form.watch("managedOrganizations");

  const organizationsQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_NAMES],
    queryFn: async () => {
      return await organizationsRepository.getOrganizationNames();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000 + 1,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  const organizations: OrganizationName[] = organizationsQuery.data ?? [];

  // Reset form when user prop changes
  useEffect(() => {
    if (user) {
      form.reset({
        username: user.username,
        email: user.email ?? "",
        role: user.role,
        managedOrganizations: user.managedOrganizations ?? [],
      });
    }
  }, [user]);

  // Clear managed organizations when role changes to Viewer
  useEffect(() => {
    if (watchedRole === UserRole.Viewer) {
      form.setValue("managedOrganizations", []);
    }
  }, [watchedRole]);

  const onSubmit = async (data: EditUserFormValues) => {
    if (!user) return;
    try {
      await onEditUser({
        ...user,
        username: data.username,
        email: data.email,
        role: data.role,
        managedOrganizations: data.managedOrganizations,
      });
      handleClose();
    } catch (error) {
      const rootMsg = setApiErrors(error, form.setError, { username: "username" });
      if (rootMsg) {
        form.setError("root", { message: rootMsg });
      }
    }
  };

  // Derive assigned organizations from the form's managedOrganizations array
  const assignedOrganizations = organizations.filter(
    (org) => watchedManagedOrgs.includes(org.id)
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="min-w-[30%] flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
        </DialogHeader>
        <Separator />
        {form.formState.errors.root && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {form.formState.errors.root.message}
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 mb-1">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Role</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", colorVariantClasses[UserRoleColors[field.value]]?.text)}>
                        <SelectValue placeholder="User role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {UserRoleSelectOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          <span className={cn(option.color && colorVariantClasses[option.color]?.text)}>
                            {option.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Username</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Email address</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="managedOrganizations"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel className="text-sm">Assigned organizations</FormLabel>
                  <FormControl>
                    <GenericMultipleSelect
                      disabled={watchedRole === UserRole.Viewer}
                      inputValues={assignedOrganizations.map(org => ({ id: org.id, label: org.name }))}
                      options={organizations.map(org => ({ id: org.id, label: org.name }))}
                      placeholder="Choose organizations"
                      setValues={(selectedOptions) => {
                        field.onChange(
                          selectedOptions.length > 0
                            ? selectedOptions.map((x) => x.id)
                            : []
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                type="button"
                onClick={handleClose}
                disabled={form.formState.isSubmitting}
                className="mr-2 w-20"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-20"
              >
                {form.formState.isSubmitting ? "..." : "Edit"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
